import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Newspaper, Users, Shield, Building2, Briefcase, Tag, Rocket, LogOut, Settings, UserX, X, ChevronRight, Sparkles } from 'lucide-react';
import { useAuth } from '../../lib/auth-context';
import ReactDOM from 'react-dom';

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
  onAdminClick?: () => void;
  isAdmin?: boolean;
}

export function Menu({ isOpen, onClose, onAdminClick, isAdmin }: MenuProps) {
  const { user, userInfo, logout } = useAuth();
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleNavigation = (path: string) => {
    onClose();
    setTimeout(() => {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 250);
  };

  const menuItems = [
    { name: 'Actualites', icon: Newspaper, path: '/content', color: 'from-sky-500 to-blue-600', bgLight: 'bg-sky-50 dark:bg-sky-900/20', textColor: 'text-sky-600 dark:text-sky-400', gradient: 'from-sky-400/20 to-blue-500/20' },
    { name: 'Bons plans', icon: Tag, path: 'https://pvtistes.net/bonsplans/', external: true, highlight: true, color: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50 dark:bg-amber-900/20', textColor: 'text-amber-600 dark:text-amber-400', gradient: 'from-amber-400/20 to-orange-500/20' },
    { name: 'Recits de Pvtistes', icon: Users, path: '/interviews', color: 'from-teal-500 to-emerald-600', bgLight: 'bg-teal-50 dark:bg-teal-900/20', textColor: 'text-teal-600 dark:text-teal-400', gradient: 'from-teal-400/20 to-emerald-500/20' },
    { name: 'Offres d\'emploi', icon: Briefcase, path: '/jobs', color: 'from-blue-500 to-cyan-600', bgLight: 'bg-blue-50 dark:bg-blue-900/20', textColor: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-400/20 to-cyan-500/20' },
    { name: 'Assurance PVT', icon: Shield, path: '/assurance', color: 'from-emerald-500 to-green-600', bgLight: 'bg-emerald-50 dark:bg-emerald-900/20', textColor: 'text-emerald-600 dark:text-emerald-400', gradient: 'from-emerald-400/20 to-green-500/20' },
    { name: 'Ateliers de preparation', icon: Rocket, path: 'https://pvtistes.net/bonsplans/atelier-preparation-depart-pvt/', external: true, color: 'from-rose-500 to-pink-600', bgLight: 'bg-rose-50 dark:bg-rose-900/20', textColor: 'text-rose-600 dark:text-rose-400', gradient: 'from-rose-400/20 to-pink-500/20' },
    { name: 'Ambassades et consulats', icon: Building2, path: '/ambassades', color: 'from-slate-500 to-gray-600', bgLight: 'bg-slate-50 dark:bg-slate-900/20', textColor: 'text-slate-600 dark:text-slate-400', gradient: 'from-slate-400/20 to-gray-500/20' },
  ];

  const displayName = userInfo?.user?.username || user?.displayName || '';

  if (!isOpen) return null;

  const menuContent = (
    <AnimatePresence>
      <div className="fixed inset-0" style={{ zIndex: 9999 }}>
        <motion.div
          initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
          animate={{ opacity: 1, backdropFilter: 'blur(12px)' }}
          exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.2, 1], opacity: [0, 0.6, 0] }}
            transition={{ duration: 0.6, times: [0, 0.5, 1] }}
            className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-cyan-500/30"
          />
        </motion.div>

        <motion.div
          initial={{ y: '100%', scale: 0.95, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: '100%', scale: 0.95, opacity: 0 }}
          transition={{
            type: 'spring',
            damping: 30,
            stiffness: 300,
            mass: 0.8,
            opacity: { duration: 0.2 }
          }}
          className="absolute bottom-0 left-0 right-0 lg:top-0 lg:right-0 lg:left-auto lg:bottom-0 lg:w-[420px] bg-white dark:bg-gray-900 flex flex-col rounded-t-[2rem] lg:rounded-none shadow-2xl overflow-hidden"
          style={{
            maxHeight: 'calc(100vh - 2rem)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
        >
          <motion.div
            initial={{ scaleX: 0.5, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="flex items-center justify-center pt-3 pb-1 lg:hidden"
          >
            <div className="w-12 h-1.5 rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 shadow-lg shadow-emerald-500/50" />
          </motion.div>

          <div className="relative px-5 py-4 lg:py-5 overflow-hidden">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.1, scale: 1 }}
              className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-400 blur-3xl"
            />
            <div className="relative flex items-center justify-between">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.15, duration: 0.3 }}
                className="flex items-center gap-3"
              >
                <img
                  src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_positive_fullcolor.png"
                  alt="Pvtistes"
                  className="h-8 w-auto dark:hidden"
                />
                <img
                  src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_negative_white.png"
                  alt="Pvtistes"
                  className="h-8 w-auto hidden dark:block"
                />
              </motion.div>
              <motion.button
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 300, damping: 20 }}
                onClick={onClose}
                className="relative p-2.5 rounded-xl transition-all duration-300 group overflow-hidden"
                aria-label="Fermer le menu"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-rose-500/0 group-hover:from-red-500/10 group-hover:to-rose-500/10 transition-all duration-300" />
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-red-500 dark:group-hover:text-red-400 relative z-10 transition-colors group-hover:rotate-90 duration-300" />
              </motion.button>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />
          </div>

          {user && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.25, duration: 0.4, type: 'spring', stiffness: 300 }}
              className="mx-4 mt-4 mb-2"
            >
              <div className="relative group overflow-hidden rounded-2xl p-[1px] bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                <div className="relative flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-gray-900">
                  <div className="relative flex-shrink-0">
                    <motion.div
                      animate={{
                        boxShadow: [
                          '0 0 0 0 rgba(16, 185, 129, 0.4)',
                          '0 0 0 8px rgba(16, 185, 129, 0)',
                          '0 0 0 0 rgba(16, 185, 129, 0.4)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-base shadow-xl"
                    >
                      {displayName?.[0]?.toUpperCase()}
                    </motion.div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.4, type: 'spring' }}
                      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white dark:border-gray-900 flex items-center justify-center"
                    >
                      <Sparkles className="w-2.5 h-2.5 text-white" />
                    </motion.div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 font-semibold">
                      Membre Pvtistes
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <nav className="flex-1 overflow-y-auto px-4 pt-3 pb-4 custom-scrollbar">
            <div className="space-y-2">
              {menuItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.05 * index + 0.3,
                    duration: 0.4,
                    type: 'spring',
                    stiffness: 300,
                    damping: 25
                  }}
                  onHoverStart={() => setHoveredIndex(index)}
                  onHoverEnd={() => setHoveredIndex(null)}
                  className="relative"
                >
                  {hoveredIndex === index && (
                    <motion.div
                      layoutId="menuHover"
                      className={`absolute inset-0 bg-gradient-to-r ${item.gradient} rounded-2xl -z-10 blur-xl`}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  {item.external ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onClose}
                      className="relative flex items-center gap-3.5 px-4 py-3.5 rounded-2xl transition-all duration-300 active:scale-[0.97] group overflow-hidden"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                      <div className="absolute inset-0 bg-gray-50/50 dark:bg-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className={`relative w-11 h-11 rounded-xl ${item.bgLight} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-lg transition-shadow`}
                      >
                        <item.icon className={`w-5 h-5 ${item.textColor} transition-transform group-hover:scale-110`} />
                      </motion.div>

                      <span className="relative flex-1 text-[15px] font-bold text-gray-800 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                        {item.name}
                      </span>

                      <motion.svg
                        animate={{ opacity: hoveredIndex === index ? 1 : 0.6 }}
                        className="relative w-4 h-4 text-gray-400 dark:text-gray-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <motion.path
                          animate={{ pathLength: hoveredIndex === index ? 1 : 0.8 }}
                          d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path d="M15 3h6v6" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 14L21 3" strokeLinecap="round" strokeLinejoin="round"/>
                      </motion.svg>
                    </a>
                  ) : (
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className="relative flex items-center gap-3.5 w-full px-4 py-3.5 text-left rounded-2xl transition-all duration-300 active:scale-[0.97] group overflow-hidden"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                      <div className="absolute inset-0 bg-gray-50/50 dark:bg-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                        className={`relative w-11 h-11 rounded-xl ${item.bgLight} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-lg transition-shadow`}
                      >
                        <item.icon className={`w-5 h-5 ${item.textColor} transition-transform group-hover:scale-110`} />
                      </motion.div>

                      <span className="relative flex-1 text-[15px] font-bold text-gray-800 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                        {item.name}
                      </span>

                      <motion.div
                        animate={{
                          x: hoveredIndex === index ? 4 : 0,
                          opacity: hoveredIndex === index ? 1 : 0.4
                        }}
                        transition={{ type: 'spring', stiffness: 400 }}
                      >
                        <ChevronRight className="relative w-5 h-5 text-gray-400 dark:text-gray-500" />
                      </motion.div>
                    </button>
                  )}
                </motion.div>
              ))}
            </div>

            <div className="mt-4 pt-4 space-y-2 relative">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent" />

              {isAdmin && onAdminClick && (
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, type: 'spring' }}
                  onClick={() => {
                    onAdminClick();
                    onClose();
                  }}
                  className="relative flex items-center gap-3 w-full px-4 py-3 text-left rounded-2xl transition-all duration-300 active:scale-[0.97] group overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-400/10 to-slate-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute inset-0 bg-gray-50/50 dark:bg-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <motion.div
                    whileHover={{ rotate: 180, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className="relative w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 group-hover:shadow-lg transition-shadow"
                  >
                    <Settings className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors" />
                  </motion.div>
                  <span className="relative text-[15px] font-bold text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Administration</span>
                </motion.button>
              )}

              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.65, type: 'spring' }}
                onClick={() => {
                  logout();
                  onClose();
                }}
                className="relative flex items-center gap-3 w-full px-4 py-3 text-left rounded-2xl transition-all duration-300 active:scale-[0.97] group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gray-50/50 dark:bg-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                  className="relative w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 group-hover:shadow-lg transition-shadow"
                >
                  <LogOut className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors" />
                </motion.div>
                <span className="relative text-[15px] font-bold text-gray-700 dark:text-gray-300 group-hover:text-orange-700 dark:group-hover:text-orange-400 transition-colors">Deconnexion</span>
              </motion.button>

              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, type: 'spring' }}
                onClick={() => handleNavigation('/delete-account')}
                className="relative flex items-center gap-3 w-full px-4 py-3 text-left rounded-2xl transition-all duration-300 active:scale-[0.97] group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 to-rose-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-red-50/50 dark:bg-red-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                  className="relative w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-red-500/20 transition-shadow"
                >
                  <UserX className="w-5 h-5 text-red-500 dark:text-red-400 group-hover:scale-110 transition-transform" />
                </motion.div>
                <span className="relative text-[15px] font-bold text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors">Supprimer mon compte</span>
              </motion.button>
            </div>
          </nav>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(menuContent, document.body);
}
